import React, { useState, useEffect } from 'react';
import { Plus, Package, Edit, Trash2, Search, AlertCircle, ShoppingBag, Check } from 'lucide-react';
import api from '../services/api';

const Catalog = () => {
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<any>({
        name: '',
        category: 'Accessoires',
        price: '',
        stock_quantity: 0,
        low_stock_threshold: 5,
        image_url: ''
    });
    const [editingItemId, setEditingItemId] = useState<number | null>(null);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await api.get('/products');
            setProducts(response.data || []);
        } catch (error) {
            console.error('Erreur de chargement des produits', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleOpenCreateModal = () => {
        setFormData({
            name: '',
            category: 'Accessoires',
            price: '',
            stock_quantity: 10,
            low_stock_threshold: 5,
            image_url: ''
        });
        setEditingItemId(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (item: any) => {
        setFormData({
            name: item.name || '',
            category: item.category || 'Accessoires',
            price: item.price || '',
            stock_quantity: item.stock_quantity ?? 0,
            low_stock_threshold: item.low_stock_threshold ?? 5,
            image_url: item.image_url || ''
        });
        setEditingItemId(item.id);
        setIsModalOpen(true);
    };

    const handleDeleteProduct = async (id: number, name: string) => {
        if (window.confirm(`Voulez-vous vraiment supprimer l'article "${name}" du catalogue ?`)) {
            try {
                await api.delete(`/products/${id}`);
                fetchProducts();
            } catch (error: any) {
                console.error('Erreur lors de la suppression', error);
                alert(error.response?.data?.error || 'Erreur lors de la suppression');
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                name: formData.name.trim(),
                category: formData.category,
                price: parseFloat(formData.price),
                stock_quantity: parseInt(formData.stock_quantity, 10) || 0,
                low_stock_threshold: parseInt(formData.low_stock_threshold, 10) || 5,
                image_url: formData.image_url ? formData.image_url.trim() : null
            };

            if (editingItemId) {
                await api.put(`/products/${editingItemId}`, payload);
            } else {
                await api.post('/products', payload);
            }

            setIsModalOpen(false);
            setFormData({});
            setEditingItemId(null);
            fetchProducts();
        } catch (error: any) {
            console.error("Erreur de sauvegarde", error);
            alert(error.response?.data?.error || "Erreur lors de l'enregistrement de l'article.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Filtrage des articles
    const filteredProducts = products.filter(p => {
        const matchesSearch = 
            p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.category && p.category.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const categories = Array.from(new Set(products.map(p => p.category).filter(Boolean)));

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black text-brand-dark flex items-center gap-3">
                        <ShoppingBag className="text-brand-primary" size={32} />
                        Articles & Boutique
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Gestion des produits en vente, inventaire et réapprovisionnement de la boutique.
                    </p>
                </div>
                <button
                    onClick={handleOpenCreateModal}
                    className="flex items-center gap-2 px-5 py-2.5 bg-brand-primary hover:bg-yellow-400 text-brand-dark font-black rounded-xl transition-all shadow-sm cursor-pointer"
                >
                    <Plus size={18} />
                    Ajouter un article
                </button>
            </div>

            {/* Conteneur principal */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Barre de recherche et filtre */}
                <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3 items-center justify-between bg-gray-50/50">
                    <div className="relative w-full sm:w-80">
                        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher un article..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none"
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <span className="text-xs font-bold text-gray-500 uppercase">Catégorie :</span>
                        <select
                            value={categoryFilter}
                            onChange={e => setCategoryFilter(e.target.value)}
                            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-primary outline-none"
                        >
                            <option value="all">Toutes les catégories</option>
                            {categories.map((cat: any) => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Table des articles */}
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-12 text-center text-gray-400">Chargement des articles...</div>
                    ) : filteredProducts.length === 0 ? (
                        <div className="p-12 text-center">
                            <Package size={44} className="mx-auto text-gray-300 mb-2" />
                            <p className="text-gray-500 font-medium">Aucun article trouvé.</p>
                            <button
                                onClick={handleOpenCreateModal}
                                className="mt-3 text-xs font-bold text-brand-dark bg-brand-primary px-3 py-1.5 rounded-lg"
                            >
                                + Créer un article
                            </button>
                        </div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b">
                                <tr>
                                    <th className="px-6 py-4">Image</th>
                                    <th className="px-6 py-4">Article</th>
                                    <th className="px-6 py-4">Catégorie</th>
                                    <th className="px-6 py-4">Prix</th>
                                    <th className="px-6 py-4">Stock</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map(item => {
                                    const isLowStock = item.stock_quantity <= (item.low_stock_threshold || 5);
                                    return (
                                        <tr key={item.id} className="border-b hover:bg-gray-50/80 transition-colors">
                                            <td className="px-6 py-4">
                                                {item.image_url ? (
                                                    <img src={item.image_url} alt={item.name} className="w-12 h-12 object-cover rounded-xl border border-gray-200 shadow-2xs" />
                                                ) : (
                                                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 border border-gray-200">
                                                        <Package size={20} />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 font-black text-gray-900">
                                                {item.name}
                                            </td>
                                            <td className="px-6 py-4 text-gray-600 font-medium">
                                                <span className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs">
                                                    {item.category || 'Général'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 font-black text-brand-dark">
                                                {Number(item.price).toLocaleString('fr-FR')} FCFA
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className={`font-black ${isLowStock ? 'text-red-600' : 'text-emerald-700'}`}>
                                                        {item.stock_quantity}
                                                    </span>
                                                    {isLowStock && (
                                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                                                            Alerte seuil ({item.low_stock_threshold})
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="inline-flex items-center gap-1.5">
                                                    <button
                                                        onClick={() => handleOpenEditModal(item)}
                                                        className="p-2 text-gray-600 hover:text-brand-dark hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                                                        title="Modifier l'article"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteProduct(item.id, item.name)}
                                                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                                        title="Supprimer l'article"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* MODALE AJOUT / MODIFICATION D'ARTICLE */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
                        <div className="flex justify-between items-center border-b border-gray-100 pb-3 mb-5">
                            <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
                                <Package size={20} className="text-brand-primary" />
                                {editingItemId ? "Modifier l'article" : "Ajouter un nouvel article"}
                            </h2>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 text-sm font-bold"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                    Nom de l'article *
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Ex: Gant sport, Shaker, Whey..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary text-sm font-medium"
                                    value={formData.name || ''}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                        Catégorie *
                                    </label>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary text-sm font-medium"
                                        value={formData.category || 'Accessoires'}
                                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                                    >
                                        <option value="Accessoires">Accessoires</option>
                                        <option value="Equipement">Equipement</option>
                                        <option value="Nutrition">Nutrition</option>
                                        <option value="Boissons">Boissons</option>
                                        <option value="Vêtements">Vêtements</option>
                                        <option value="Autre">Autre</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                        Prix (FCFA) *
                                    </label>
                                    <input
                                        type="number"
                                        step="1"
                                        required
                                        placeholder="Ex: 5000"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary text-sm font-bold"
                                        value={formData.price || ''}
                                        onChange={e => setFormData({ ...formData, price: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                        Quantité en Stock *
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary text-sm"
                                        value={formData.stock_quantity ?? ''}
                                        onChange={e => setFormData({ ...formData, stock_quantity: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                        Seuil minimal d'alerte *
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary text-sm"
                                        value={formData.low_stock_threshold ?? 5}
                                        onChange={e => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                    URL de l'image (optionnel)
                                </label>
                                <input
                                    type="text"
                                    placeholder="https://..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary text-xs"
                                    value={formData.image_url || ''}
                                    onChange={e => setFormData({ ...formData, image_url: e.target.value })}
                                />
                            </div>

                            <div className="pt-3 border-t border-gray-100 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold text-xs transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2 bg-brand-primary hover:bg-yellow-400 text-brand-dark font-black rounded-xl text-xs transition-colors shadow-sm disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Enregistrement...' : (editingItemId ? 'Modifier l\'article' : 'Créer l\'article')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Catalog;
